/**
 * Keep table-visualization in sync when catalog section/table rows change
 * from the Tables / Sections screens (or any catalog CRUD).
 *
 * Best-effort: catalog writes must not fail if visualization is missing/errors.
 */

import {
  createDefaultSectionLayout,
  createDefaultTablePlacement,
  deepCloneFloorPlan,
  fetchCurrentFloorPlan,
  resolvePresetForCapacity,
  saveFloorPlanToCatalog,
  suggestTableSizeForHall,
} from './floorPlanStorage';

const labelOf = (name, displayName, fallback) => {
  const display =
    typeof displayName === 'string' && displayName.trim()
      ? displayName.trim()
      : '';
  const base = typeof name === 'string' && name.trim() ? name.trim() : '';
  return display || base || fallback;
};

const mutateAndSave = async (businessUuid, mutate) => {
  if (!businessUuid) {
    return null;
  }
  const { plan } = await fetchCurrentFloorPlan(businessUuid);
  const next = deepCloneFloorPlan(plan);
  const changed = mutate(next);
  if (!changed) {
    return plan;
  }
  const result = await saveFloorPlanToCatalog({
    businessUuid,
    plan: next,
  });
  return result.snapshot;
};

/** Ensure a catalog section exists as a floor-plan section layout. */
export const ensureSectionInVisualization = async ({
  businessUuid,
  uuid,
  name,
  displayName,
  updateIdentity = true,
}) => {
  if (!uuid) {
    return null;
  }
  const label = labelOf(name, displayName, 'Section');
  try {
    return await mutateAndSave(businessUuid, (plan) => {
      const existing = plan.sections?.[uuid];
      if (existing) {
        if (!updateIdentity) {
          return false;
        }
        if (existing.name === label && existing.displayName === label) {
          return false;
        }
        plan.sections[uuid] = {
          ...existing,
          name: label,
          displayName: label,
        };
        return true;
      }
      plan.sections = {
        ...(plan.sections || {}),
        [uuid]: {
          ...createDefaultSectionLayout(),
          name: label,
          displayName: label,
        },
      };
      return true;
    });
  } catch {
    return null;
  }
};

/** Ensure a catalog table exists as a placement under its section. */
export const ensureTableInVisualization = async ({
  businessUuid,
  uuid,
  sectionUuid,
  name,
  displayName,
  capacity,
}) => {
  if (!uuid || !sectionUuid) {
    return null;
  }
  const label = labelOf(name, displayName, 'Table');
  const seats =
    typeof capacity === 'number' && Number.isFinite(capacity) ? capacity : 4;

  try {
    // Section layout must exist in viz first (do not overwrite its name).
    await ensureSectionInVisualization({
      businessUuid,
      uuid: sectionUuid,
      name: 'Section',
      displayName: 'Section',
      updateIdentity: false,
    });

    return await mutateAndSave(businessUuid, (plan) => {
      if (!plan.sections?.[sectionUuid]) {
        plan.sections = {
          ...(plan.sections || {}),
          [sectionUuid]: createDefaultSectionLayout(),
        };
      }

      // Move across sections if needed.
      Object.entries(plan.sections || {}).forEach(([secUuid, layout]) => {
        if (secUuid === sectionUuid || !layout?.tables?.[uuid]) {
          return;
        }
        delete layout.tables[uuid];
      });

      const layout = plan.sections[sectionUuid];
      const existing = layout.tables?.[uuid];
      if (existing) {
        layout.tables[uuid] = {
          ...existing,
          name: label,
          displayName: label,
          capacity: seats,
        };
        return true;
      }

      const preset = resolvePresetForCapacity(seats);
      const sized = suggestTableSizeForHall(
        preset.width,
        preset.height,
        layout.canvas?.width || 1200,
        layout.canvas?.height || 800,
        Object.keys(layout.tables || {}).length + 1
      );
      const index = Object.keys(layout.tables || {}).length;
      const col = index % 4;
      const row = Math.floor(index / 4);
      const gap = 24;
      const x = 40 + col * (sized.width + gap);
      const y = 40 + row * (sized.height + gap);

      layout.tables = {
        ...(layout.tables || {}),
        [uuid]: {
          ...createDefaultTablePlacement(
            preset.shape,
            sized.width,
            sized.height,
            x,
            y,
            index + 1
          ),
          name: label,
          displayName: label,
          capacity: seats,
        },
      };
      return true;
    });
  } catch {
    return null;
  }
};

export const updateSectionInVisualization = async ({
  businessUuid,
  uuid,
  name,
  displayName,
}) =>
  ensureSectionInVisualization({
    businessUuid,
    uuid,
    name,
    displayName,
  });

export const updateTableInVisualization = async ({
  businessUuid,
  uuid,
  sectionUuid,
  name,
  displayName,
  capacity,
}) =>
  ensureTableInVisualization({
    businessUuid,
    uuid,
    sectionUuid,
    name,
    displayName,
    capacity,
  });

export const removeSectionFromVisualization = async ({
  businessUuid,
  uuid,
}) => {
  if (!uuid) {
    return null;
  }
  try {
    return await mutateAndSave(businessUuid, (plan) => {
      if (!plan.sections?.[uuid]) {
        return false;
      }
      delete plan.sections[uuid];
      return true;
    });
  } catch {
    return null;
  }
};

export const removeTableFromVisualization = async ({ businessUuid, uuid }) => {
  if (!uuid) {
    return null;
  }
  try {
    return await mutateAndSave(businessUuid, (plan) => {
      let changed = false;
      Object.values(plan.sections || {}).forEach((layout) => {
        if (layout?.tables?.[uuid]) {
          delete layout.tables[uuid];
          changed = true;
        }
      });
      return changed;
    });
  } catch {
    return null;
  }
};
